import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { Map, Network, Table2, RefreshCw, Move, Lock, RotateCcw } from 'lucide-react'
import api from '../services/api'
import KnowledgeMapCanvas from '../components/knowledge-map/KnowledgeMapCanvas'
import StudentGraphLegend from '../components/knowledge-map/StudentGraphLegend'
import NodeDetailPanel from '../components/knowledge-map/NodeDetailPanel'
import KnowledgeMapExport from '../components/knowledge-map/KnowledgeMapExport'
import KnowledgeNodeTable from '../components/knowledge-map/KnowledgeNodeTable'
import MasteryTooltip from '../components/knowledge-map/MasteryTooltip'
import KnowledgeGraphControls from '../components/knowledge-map/shared/KnowledgeGraphControls'
import KnowledgeGraphStatsFooter from '../components/knowledge-map/shared/KnowledgeGraphStatsFooter'
import {
  STATUS_COLORS,
  DIFFICULTY_LABELS,
  buildSkillGraphNodes
} from '../components/knowledge-map/knowledgeMapUtils'
import { parseJsonObject } from '../utils/parseJsonObject'
import { useAuthStore } from '../stores/authStore'
import { useFillViewportHeight } from '../hooks/useFillViewportHeight'
import { createLogger } from '../utils/logger'

const logger = createLogger('KnowledgeMap')

const DEFAULT_FILTERS = { statuses: [], domain: null, difficulty: null }
const DIFF_ORDER = { E: 0, M: 1, H: 2 }
const STATUS_ORDER = { UNVISITED: 0, STRUGGLING: 1, IN_PROGRESS: 2, MASTERED: 3 }

// Tutor-style filter arrangement: Domain, Difficulty, Status, Search. Node coloring stays the
// learner's personal mastery (STATUS_COLORS), unlike the admin graph's test-type/difficulty coloring.
const STATUS_OPTIONS = [
  ['MASTERED', 'Mastered'],
  ['IN_PROGRESS', 'In progress'],
  ['STRUGGLING', 'Struggling'],
  ['UNVISITED', 'Unvisited']
]

// Build the stats-footer groups for the personal map: skill count + the learner's mastery
// distribution across the visible skills (one node per skill).
function buildStudentStatsGroups(skillNodes) {
  const c = { MASTERED: 0, IN_PROGRESS: 0, STRUGGLING: 0, UNVISITED: 0 }
  skillNodes.forEach((n) => {
    const s = n.mastery?.status || 'UNVISITED'
    if (c[s] !== undefined) c[s] += 1
  })
  return [
    [{ value: skillNodes.length, label: skillNodes.length === 1 ? 'skill' : 'skills' }],
    [
      { value: c.MASTERED, label: 'Mastered', color: 'text-green-600 dark:text-green-400' },
      { value: c.IN_PROGRESS, label: 'In progress', color: 'text-amber-600 dark:text-amber-400' },
      { value: c.STRUGGLING, label: 'Struggling', color: 'text-red-600 dark:text-red-400' },
      { value: c.UNVISITED, label: 'Unvisited', color: 'text-gray-500 dark:text-gray-400' }
    ]
  ]
}

// Persist the view config (view/graph mode, type/layout/edges, filters) so the view survives
// navigation/reload. Scoped by child in the guardian view so per-child views stay independent.
const gridKey = (keyPrefix) => `${keyPrefix}:grid`
const loadGrid = (keyPrefix) => {
  try {
    return parseJsonObject(localStorage.getItem(gridKey(keyPrefix))) || {}
  } catch {
    return {}
  }
}

// Mastery status pill used in the table.
function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.UNVISITED
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.dot }} />
      {c.text}
    </span>
  )
}

StatusPill.propTypes = { status: PropTypes.string }

const TABLE_COLUMNS = [
  {
    key: 'skill',
    label: 'Skill',
    render: (n) => <span className="font-medium text-gray-900 dark:text-gray-100">{n.skill}</span>
  },
  { key: 'domain', label: 'Domain' },
  {
    key: 'difficulty',
    label: 'Difficulty',
    render: (n) => DIFFICULTY_LABELS[n.difficulty] || n.difficulty,
    sortValue: (n) => DIFF_ORDER[n.difficulty] ?? 9
  },
  {
    key: 'status',
    label: 'Mastery',
    render: (n) => <StatusPill status={n.mastery?.status || 'UNVISITED'} />,
    sortValue: (n) => STATUS_ORDER[n.mastery?.status || 'UNVISITED'] ?? 0,
    // "Not visited" means no data, not the worst score — keep it at the bottom in both directions.
    alwaysLast: (n) => (n.mastery?.status || 'UNVISITED') === 'UNVISITED'
  },
  {
    key: 'proficiency',
    label: 'Proficiency',
    align: 'right',
    render: (n) =>
      n.mastery?.totalAttempts > 0 ? `${Math.round((n.mastery.score || 0) * 100)}%` : '—',
    sortValue: (n) => n.mastery?.score || 0
  }
]

/**
 * Personal knowledge map. Renders the student's own map by default; in the guardian view it renders a
 * child's map READ-ONLY when `childId` is provided (data then comes from the family endpoints and
 * layout editing is suppressed).
 *
 * @param {string} [childId]  - render this child's map from the family endpoints (guardian view)
 * @param {string} [userName] - name used on the export image (defaults to the signed-in user)
 * @param {boolean} [embedded] - when true, skip the outer page padding (rendered inside another page)
 */
export default function KnowledgeMap({ childId = null, userName, embedded = false }) {
  const user = useAuthStore((state) => state.user)
  const readOnly = !!childId
  // Base path for the learner-scoped fetches (nodes/stats/node content). The student endpoints for
  // the signed-in user, or the family endpoints for a specific child.
  const apiBase = childId ? `/family/children/${childId}/knowledge-map` : '/knowledge-map'
  // Storage namespace: separate per child so a guardian's per-child views/layouts never collide.
  const keyPrefix = childId ? `family:${childId}:knowledge-map` : 'student:knowledge-map'

  // Restore the persisted grid config once on mount (single combined map, no per-subject key).
  const [savedGrid] = useState(() => loadGrid(keyPrefix))
  const [nodes, setNodes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [hoveredNode, setHoveredNode] = useState(null)
  const [selectedNode, setSelectedNode] = useState(null)
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...(savedGrid.filters || {}) })
  const [searchQuery, setSearchQuery] = useState('')
  // Type filter (All / Math / Reading & Writing) — replaces the old subject picker; both subjects are
  // loaded together and this narrows the visible set client-side, matching the admin graph.
  const [typeFilter, setTypeFilter] = useState(savedGrid.typeFilter || 'all')
  const [layoutMode, setLayoutMode] = useState(savedGrid.layoutMode || 'domain')
  const [edgeVisibility, setEdgeVisibility] = useState(savedGrid.edgeVisibility || 'prerequisites')
  const [viewMode, setViewMode] = useState(savedGrid.viewMode === 'table' ? 'table' : 'graph')
  // 'skill' (default): one node per skill with the DOK strip; 'full': one node per skill×difficulty.
  const [graphMode, setGraphMode] = useState(savedGrid.graphMode === 'full' ? 'full' : 'skill')
  const [editLayout, setEditLayout] = useState(false)
  const [layoutVersion, setLayoutVersion] = useState(0)

  // Size the Table view to fill the viewport (like the graph canvas already does) so the table uses
  // the vertical space and the detail pane — which spans this content region — is full-height.
  const tableFillRef = useRef(null)
  const tableFillHeight = useFillViewportHeight(tableFillRef, {
    enabled: viewMode === 'table'
  })

  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Load the learner's map for BOTH subjects at once (merged); the Type filter narrows client-side,
  // so there is no subject picker. Each node is tagged with its testType so that filter can apply.
  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [mathRes, rwRes] = await Promise.all([
        api.get(`${apiBase}/math`),
        api.get(`${apiBase}/reading_writing`)
      ])
      if (!mountedRef.current) return
      const tag = (arr, t) => (arr || []).map((n) => ({ ...n, testType: n.testType || t }))
      setNodes([
        ...tag(mathRes.data.data.nodes, 'math'),
        ...tag(rwRes.data.data.nodes, 'reading_writing')
      ])
    } catch (err) {
      logger.error('Failed to load knowledge map nodes:', err)
      if (!mountedRef.current) return
      setError(err.response?.data?.message || err.message || 'Failed to load knowledge map')
      setLoading(false)
      return
    }
    if (!mountedRef.current) return
    setLoading(false)
  }, [apiBase])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Persist grid config whenever it changes.
  useEffect(() => {
    try {
      localStorage.setItem(
        gridKey(keyPrefix),
        JSON.stringify({ viewMode, graphMode, typeFilter, layoutMode, edgeVisibility, filters })
      )
    } catch {
      /* ignore quota errors */
    }
  }, [keyPrefix, viewMode, graphMode, typeFilter, layoutMode, edgeVisibility, filters])

  // Escape closes the detail panel.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectedNode(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Apply the Type filter to the base node set before deriving skill nodes / table rows / domains.
  const typedNodes = useMemo(
    () => (typeFilter === 'all' ? nodes : nodes.filter((n) => n.testType === typeFilter)),
    [nodes, typeFilter]
  )

  const domains = useMemo(
    () => [...new Set(typedNodes.map((n) => n.domain).filter(Boolean))].sort(),
    [typedNodes]
  )

  // Consolidated one-node-per-skill view (default). The full skill×difficulty set stays available
  // behind the graph-mode toggle; the table always works on the full set.
  const skillNodes = useMemo(() => buildSkillGraphNodes(typedNodes), [typedNodes])
  const graphNodes = graphMode === 'skill' ? skillNodes : typedNodes

  // Hard-filtered node set for the table (graph dims via opacity to keep its layout stable).
  const tableRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return typedNodes.filter((n) => {
      const status = n.mastery?.status || 'UNVISITED'
      if (filters.statuses.length && !filters.statuses.includes(status)) return false
      if (filters.domain && n.domain !== filters.domain) return false
      if (filters.difficulty && n.difficulty !== filters.difficulty) return false
      if (q && !`${n.skill} ${n.domain}`.toLowerCase().includes(q)) return false
      return true
    })
  }, [typedNodes, filters, searchQuery])

  const handleResetLayout = useCallback(() => {
    try {
      // Clear the saved positions for the graph flavor being edited (skill vs full keys differ).
      const suffix = graphMode === 'skill' ? ':skill' : ''
      localStorage.removeItem(`${keyPrefix}:layout:${layoutMode}${suffix}`)
    } catch {
      /* ignore */
    }
    setLayoutVersion((v) => v + 1)
  }, [keyPrefix, layoutMode, graphMode])

  const navigateToNode = useCallback(
    (key) => {
      // Resolve within the node set matching the key's granularity (skill vs skill×difficulty).
      const target =
        skillNodes.find((n) => n.skillGroupKey === key) ||
        typedNodes.find((n) => n.skillGroupKey === key)
      if (target) setSelectedNode(target)
    },
    [typedNodes, skillNodes]
  )

  return (
    // --panel-w is the single source of truth for the detail-panel width: the panel reads it for its
    // max width and the content wrapper reserves the same amount on wide screens, so the two can't drift.
    <div
      className={embedded ? '' : 'max-w-full mx-auto px-4 py-4'}
      style={{ '--panel-w': '30rem' }}
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Map className="w-6 h-6" /> Knowledge Map
          </h1>
          <span className="hidden sm:inline text-sm text-gray-400 font-normal">
            Click a node to learn more
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Skill/Detailed graph-mode toggle (graph view only): one node per skill with the DOK
              strip, vs the full skill×difficulty graph. */}
          {viewMode === 'graph' && (
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 text-sm overflow-hidden">
              {[
                ['skill', 'Skill view'],
                ['full', 'Detailed view']
              ].map(([mode, label]) => (
                <button
                  key={mode}
                  onClick={() => {
                    setGraphMode(mode)
                    setSelectedNode(null)
                  }}
                  aria-pressed={graphMode === mode}
                  className={`px-3 py-1.5 transition-colors ${
                    graphMode === mode
                      ? 'bg-primary-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {/* Graph/Table toggle */}
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 text-sm overflow-hidden">
            {[
              ['graph', 'Graph', Network],
              ['table', 'Table', Table2]
            ].map(([mode, label, Icon]) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                aria-pressed={viewMode === mode}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 transition-colors ${
                  viewMode === mode
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
          <button
            onClick={() => loadData()}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <KnowledgeMapExport
            testType={typeFilter}
            nodes={typedNodes}
            userName={userName || user?.name}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : error ? (
        <div className="text-center py-16 text-red-500">
          <Map className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">Failed to load knowledge map</p>
          <p className="text-sm mt-1 text-gray-500">{error}</p>
          <button
            onClick={() => loadData()}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
          >
            Try Again
          </button>
        </div>
      ) : nodes.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <Map className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">No knowledge data available yet</p>
          <p className="text-sm mt-1">
            {readOnly
              ? 'This student has not completed enough challenges to populate the map yet.'
              : 'Complete some challenges to see your knowledge map populate.'}
          </p>
        </div>
      ) : (
        // The detail panel is absolutely positioned within this relative body region (below the
        // header), so it never covers the top menu. On wide screens the content reserves the panel
        // width (shared --panel-w) so the canvas/table shrink beside it; on narrow screens the panel
        // overlays the content area (its own "X" closes it), but the header stays visible above it.
        <div className="relative">
          <div
            data-testid="knowledge-map-content"
            className={`transition-[margin] duration-200 ${
              selectedNode ? 'xl:mr-[var(--panel-w)]' : ''
            }`}
          >
            {/* Shared control bar (Type / Domain / Difficulty / Status / Search + Layout / Edges),
                same as the tutor and admin graphs. Status is the student's mastery filter. */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-4">
              <KnowledgeGraphControls
                layoutMode={layoutMode}
                onLayoutChange={setLayoutMode}
                layoutModes={['domain', 'hierarchical']}
                edgeVisibility={edgeVisibility}
                onEdgeVisibilityChange={setEdgeVisibility}
                type={typeFilter}
                onTypeChange={setTypeFilter}
                domain={filters.domain || 'all'}
                onDomainChange={(v) =>
                  setFilters((f) => ({ ...f, domain: v === 'all' ? null : v }))
                }
                domainOptions={domains}
                difficulty={filters.difficulty || 'all'}
                onDifficultyChange={(v) =>
                  setFilters((f) => ({ ...f, difficulty: v === 'all' ? null : v }))
                }
                status={filters.statuses[0] || 'all'}
                onStatusChange={(v) =>
                  setFilters((f) => ({ ...f, statuses: v === 'all' ? [] : [v] }))
                }
                statusOptions={STATUS_OPTIONS}
                search={searchQuery}
                onSearchChange={setSearchQuery}
                skillMode={viewMode === 'graph' && graphMode === 'skill'}
              />
            </div>

            {viewMode === 'graph' ? (
              <>
                {/* Edit-layout controls (hidden in the read-only guardian view) */}
                {!readOnly && (
                  <div className="flex items-center justify-end gap-2 mb-2">
                    {editLayout && (
                      <button
                        onClick={handleResetLayout}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <RotateCcw className="w-4 h-4" /> Reset layout
                      </button>
                    )}
                    <button
                      onClick={() => setEditLayout((v) => !v)}
                      aria-pressed={editLayout}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                        editLayout
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {editLayout ? <Move className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                      {editLayout ? 'Editing layout, drag nodes' : 'Edit layout'}
                    </button>
                  </div>
                )}

                <KnowledgeMapCanvas
                  nodes={graphNodes}
                  filters={filters}
                  searchQuery={searchQuery}
                  layoutMode={layoutMode}
                  edgeVisibility={edgeVisibility}
                  layoutStorageKey={readOnly ? null : keyPrefix}
                  editable={!readOnly && editLayout}
                  layoutVersion={layoutVersion}
                  selectedNodeId={selectedNode?.skillGroupKey || null}
                  onNodeHover={setHoveredNode}
                  onNodeClick={(data) => setSelectedNode(data)}
                  onPaneClick={() => setSelectedNode(null)}
                  onLayoutError={(err) => {
                    logger.error('ELK layout error:', err)
                    setError('Graph layout failed. Please try refreshing.')
                  }}
                />
                <div className="mt-2">
                  <StudentGraphLegend skillMode={graphMode === 'skill'} />
                </div>
              </>
            ) : (
              // Fill the viewport height so the table scrolls internally (sticky header) instead of
              // rendering short; this also makes the detail pane (which spans this region) full-height.
              <div
                ref={tableFillRef}
                className="min-h-[400px]"
                style={tableFillHeight ? { height: tableFillHeight } : undefined}
              >
                <KnowledgeNodeTable
                  rows={tableRows}
                  columns={TABLE_COLUMNS}
                  rowKey={(n) => n.skillGroupKey}
                  onRowClick={(n) => setSelectedNode(n)}
                  storageKey={`${keyPrefix}:node-table:grid`}
                  emptyText="No skills match the current filters."
                  fillHeight
                />
              </div>
            )}

            {/* Stats footer — same position as the admin graph; the learner's own mastery counts. */}
            <div className="mt-3">
              <KnowledgeGraphStatsFooter groups={buildStudentStatsGroups(skillNodes)} />
            </div>

            {hoveredNode && !selectedNode && viewMode === 'graph' && (
              <MasteryTooltip node={hoveredNode} />
            )}
          </div>

          {selectedNode && (
            <NodeDetailPanel
              node={selectedNode}
              allNodes={selectedNode.isSkillNode ? skillNodes : nodes}
              onClose={() => setSelectedNode(null)}
              onNavigate={navigateToNode}
              nodeApiBase={apiBase}
            />
          )}
        </div>
      )}
    </div>
  )
}

KnowledgeMap.propTypes = {
  childId: PropTypes.string,
  userName: PropTypes.string,
  embedded: PropTypes.bool
}
